import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Icon, IconButton, RadioGroup, RadioButton, Textarea } from '@cbrebuild/blocks';
import Datepicker from '../../../nucleus/datepicker/datepicker';
import Select from '../../../nucleus/select/select';
import AddItem from '../../../nucleus/add-item/add-item';
import PhoneInput from '../../../nucleus/inputs/phone-input';
import { useRequiredFieldsRegistry } from './RequiredFieldsContext';
import { ReviewEditRowScope } from './ReviewEditTrackingContext';
import ConsiderationItem from '../ConsiderationTab/ConsiderationItem';
import AdditionalCommissionItem from '../ConsiderationTab/AdditionalCommissionItem';
import BrokerCommissionItem from '../CommissionsTab/BrokerCommissionItem';
import OfficeCommissionItem from '../CommissionsTab/OfficeCommissionItem';
import PoolCommissionItem from '../CommissionsTab/PoolCommissionItem';
import InstallmentItem from '../InstallmentsTab/InstallmentItem';
import OutsideBrokers from '../AllocationsTab/OutsideBrokers';
import TaxAllocations from '../AllocationsTab/TaxAllocations';
import FeeSharing from '../AllocationsTab/FeeSharing';
import Rebates from '../AllocationsTab/Rebates';
import BillToTabContainer from '../Bill-To/BillToTabContainer';
import ClientCodeAutocomplete from '../../autocomplete/client-code-autocomplete';
import NaicsAutocomplete from '../../autocomplete/naics-autocomplete';
import ContactAutocomplete from '../../autocomplete/contact-autocomplete';
import CommissionAttachmentSelector from '../../../page-templates/voucher/components/CommissionAttachmentSelector';
import { convertFreeRentMonthsToYearsAndMonths, getUpdatedFreeRentTotalMonths } from '../../../utils/free-rent-utils';
import { isValidPostal } from '../voucher-validation';
import companiesService, { defaultCompanyData } from '../../../services/companies-service';
import { search as esriSearch, buildingLookup, getEDPSuggestions } from '../../../services/esri-service';
import abbreviate from '../../../utils/state-abbreviator';
import countries from '../../../data/countries.json';
import { createConsideration } from '../../../redux/actions/considerations-actions';
import { createCommission } from '../../../redux/actions/commissions-actions';
import { addInstallment } from '../../../redux/actions/voucher/installments-actions';

const noop = () => {};
const MIN_COMPANY_SEARCH_LENGTH = 1;
const MIN_PROPERTY_SEARCH_LENGTH = 1;
const PROPERTY_SEARCH_DEBOUNCE_DELAY = 300;
const MAX_PROPERTY_SUGGESTIONS = 15;
const ESRI_COUNTRY_CODES = 'USA,CAN,CL,MX,CO,AR,BR';
// Matches the wording shown in the live voucher flow's AddressForm.js for the same
// non-digit-ZIP condition.
const ZIP_FORMAT_MESSAGE = 'Zip must be 5 digits or ZIP+4';

const normalizeLookupCandidate = (candidate) => {
  if (!candidate) return null;
  const attributes = candidate.attributes || {};
  const rawCountry = attributes.Country || '';
  const countryObj = countries.find(c => c.country_id_A3 === rawCountry)
    || countries.find(c => c.display_name === rawCountry);
  const stateAbbreviation = attributes.RegionAbbr || abbreviate(attributes.Region || '') || '';

  return {
    name: attributes.PlaceName || attributes.StAddr || '',
    address: attributes.StAddr || '',
    city: attributes.City || '',
    state_abbreviation: stateAbbreviation,
    country: countryObj ? countryObj.display_name : 'United States',
    postal_code: attributes.Country === 'CAN'
      ? `${attributes.Postal || ''} ${attributes.PostalExt || ''}`.trim()
      : (attributes.Postal || ''),
    edp_property_id: candidate.edp_property_id || null,
  };
};

const fetchPropertyLookupSuggestions = async (searchText) => {
  const esriParams = { text: searchText, countryCode: ESRI_COUNTRY_CODES, f: 'json' };

  const fetchEsriSuggestions = () => esriSearch(esriParams)
    .then(response => response?.suggestions || [])
    .catch(() => []);

  try {
    const edpResponse = await getEDPSuggestions({ search: searchText });
    const edpCandidates = (edpResponse?.results?.candidates || []).map(candidate => ({
      ...candidate,
      text: candidate.address,
      address: undefined,
    }));

    if (edpCandidates.length === 0) return fetchEsriSuggestions();
    if (edpCandidates.length >= MAX_PROPERTY_SUGGESTIONS) {
      return edpCandidates.slice(0, MAX_PROPERTY_SUGGESTIONS);
    }

    const esriSuggestions = await fetchEsriSuggestions();
    const uniqueEsriSuggestions = esriSuggestions.filter(esriSuggestion => (
      !edpCandidates.some(edpCandidate => edpCandidate.text === esriSuggestion.text)
    ));

    return edpCandidates.concat(uniqueEsriSuggestions.slice(0, MAX_PROPERTY_SUGGESTIONS - edpCandidates.length));
  } catch (_) {
    return fetchEsriSuggestions();
  }
};

const resolvePropertyLookupSelection = async (suggestion) => {
  if (!suggestion) return null;
  if (suggestion.magicKey) {
    const response = await buildingLookup({
      singleLine: suggestion.text,
      magicKey: suggestion.magicKey,
      f: 'json',
    });
    const selectedCandidate = response?.candidates?.[0];
    return normalizeLookupCandidate(selectedCandidate);
  }
  if (suggestion.attributes) {
    return normalizeLookupCandidate(suggestion);
  }
  return null;
};

const ReviewPropertyAddressSearchControl = ({
  entry,
  value,
  isSaving,
  handleChange,
  commitChange,
  getDraftValue,
}) => {
  const mountedRef = useRef(true);
  const blurFromSelectionRef = useRef(false);
  const timeoutRef = useRef(null);
  const [searchState, setSearchState] = useState({
    suggestions: [],
    showSuggestions: false,
    loading: false,
  });

  const fieldIds = entry.editTarget?.propertyLookupFieldIds || {};
  const propertySeed = entry.editTarget?.propertyLookupSeed || {};
  const activeSearchField = entry.editTarget?.propertyLookupField || 'address';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const closeSuggestions = useCallback(() => {
    if (!mountedRef.current) return;
    setSearchState(state => ({ ...state, showSuggestions: false }));
  }, []);

  const composeSearchText = useCallback(() => {
    const addressValue = (getDraftValue(fieldIds.address, propertySeed.address) || '').trim();
    const cityValue = (getDraftValue(fieldIds.city, propertySeed.city) || '').trim();
    const stateValue = (getDraftValue(fieldIds.state, propertySeed.state) || '').trim();
    const zipValue = (getDraftValue(fieldIds.zip, propertySeed.zip) || '').trim();

    if (activeSearchField === 'address') return addressValue;
    if (activeSearchField === 'city') return [addressValue, cityValue].filter(Boolean).join(' ').trim();
    if (activeSearchField === 'zip') return [addressValue, cityValue, stateValue, zipValue].filter(Boolean).join(' ').trim();
    return addressValue;
  }, [
    activeSearchField,
    fieldIds.address,
    fieldIds.city,
    fieldIds.state,
    fieldIds.zip,
    getDraftValue,
    propertySeed.address,
    propertySeed.city,
    propertySeed.state,
    propertySeed.zip,
  ]);

  const triggerSearch = useCallback((overrideText) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const searchText = (overrideText !== undefined ? overrideText : composeSearchText()).trim();
    if (!searchText || searchText.length < MIN_PROPERTY_SEARCH_LENGTH) {
      setSearchState({ suggestions: [], showSuggestions: false, loading: false });
      return;
    }

    setSearchState(state => ({ ...state, loading: true, showSuggestions: false }));

    timeoutRef.current = setTimeout(() => {
      fetchPropertyLookupSuggestions(searchText)
        .then((suggestions) => {
          if (!mountedRef.current) return;
          setSearchState({
            suggestions,
            showSuggestions: suggestions.length > 0,
            loading: false,
          });
        })
        .catch(() => {
          if (!mountedRef.current) return;
          setSearchState({ suggestions: [], showSuggestions: false, loading: false });
        });
    }, PROPERTY_SEARCH_DEBOUNCE_DELAY);
  }, [composeSearchText]);

  const applySelectionToDrafts = useCallback((payload) => {
    if (!payload) return;
    if (fieldIds.address) handleChange(fieldIds.address, payload.address || '');
    if (fieldIds.city) handleChange(fieldIds.city, payload.city || '');
    if (fieldIds.state) handleChange(fieldIds.state, payload.state_abbreviation || '');
    if (fieldIds.zip) handleChange(fieldIds.zip, payload.postal_code || '');
  }, [fieldIds.address, fieldIds.city, fieldIds.state, fieldIds.zip, handleChange]);

  const onSuggestionSelect = useCallback((suggestion) => {
    blurFromSelectionRef.current = true;
    setSearchState({ suggestions: [], showSuggestions: false, loading: false });

    resolvePropertyLookupSelection(suggestion)
      .then((payload) => {
        if (!payload || !mountedRef.current) return;
        applySelectionToDrafts(payload);
        commitChange(entry, payload, { allowEmpty: true });
      })
      .finally(() => {
        blurFromSelectionRef.current = false;
      });
  }, [applySelectionToDrafts, commitChange, entry]);

  return (
    <div className="company-name-search-wrapper required-field-row__company-search-wrapper">
      <input
        type="text"
        name="name"
        className="required-field-row__input"
        aria-label={entry.label}
        value={value || ''}
        disabled={isSaving}
        onChange={(event) => {
          const nextValue = event.target.value;
          handleChange(entry.fieldId, nextValue);
          triggerSearch(nextValue);
        }}
        onFocus={() => triggerSearch()}
        onBlur={() => {
          setTimeout(() => {
            if (mountedRef.current) closeSuggestions();
          }, 150);
          if (blurFromSelectionRef.current) return;
          // `allowEmpty: true` so clearing free-typed address text (without picking a
          // suggestion) still commits and re-triggers required validation.
          commitChange(entry, value || '', { allowEmpty: true });
        }}
      />
      {searchState.loading && <div className="company-name-search-loading-bar" />}
      {searchState.showSuggestions && searchState.suggestions.length > 0 && (
        <div className="company-name-search-dropdown">
          {searchState.suggestions.map((suggestion, index) => {
            const propertyType = suggestion?.property_type;
            const displayText = propertyType
              ? `${suggestion.text} (${propertyType})`
              : suggestion.text;
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${entry.fieldId}-${index}`}
                className="company-name-search-item"
                onMouseDown={() => onSuggestionSelect(suggestion)}
                role="option"
                aria-selected="false"
                tabIndex={-1}
              >
                <span className="company-name-search-item__name">{displayText}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

ReviewPropertyAddressSearchControl.propTypes = {
  entry: PropTypes.shape({
    fieldId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    editTarget: PropTypes.shape({
      propertyLookupField: PropTypes.string,
      propertyLookupSeed: PropTypes.shape({
        address: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
      }),
      propertyLookupFieldIds: PropTypes.shape({
        address: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
      }),
    }),
  }).isRequired,
  value: PropTypes.string,
  isSaving: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  commitChange: PropTypes.func.isRequired,
  getDraftValue: PropTypes.func.isRequired,
};

ReviewPropertyAddressSearchControl.defaultProps = {
  value: '',
};

const ReviewCompanyNameSearchControl = ({
  entry,
  value,
  isSaving,
  handleChange,
  commitChange,
}) => {
  const currentCompany = entry.editTarget?.currentCompany || {};
  const currentCompanyId = currentCompany?.id;
  const currentCompanyName = currentCompany?.name || '';
  const mountedRef = useRef(true);
  const selectionMadeRef = useRef(false);
  const savedCompanyNameRef = useRef(currentCompanyName);
  const [searchState, setSearchState] = useState({
    suggestions: [],
    showSuggestions: false,
    loading: false,
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    savedCompanyNameRef.current = currentCompanyName;
  }, [currentCompanyId, currentCompanyName]);

  const closeSuggestions = useCallback(() => {
    setSearchState(s => ({ ...s, showSuggestions: false }));
  }, []);

  const searchCompanies = useCallback(debounce((text) => {
    if (!mountedRef.current) return;
    if (!text || text.trim().length < MIN_COMPANY_SEARCH_LENGTH) {
      setSearchState(s => ({
        ...s,
        showSuggestions: false,
        suggestions: [],
        loading: false,
      }));
      return;
    }
    setSearchState(s => ({ ...s, loading: true }));
    companiesService.fetchCompanies({ search: text.trim(), page_size: 15 })
      .then((results) => {
        if (!mountedRef.current) return;
        setSearchState({
          suggestions: results,
          showSuggestions: results.length > 0,
          loading: false,
        });
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setSearchState({ suggestions: [], showSuggestions: false, loading: false });
      });
  }, 300), []);

  useEffect(() => () => searchCompanies.cancel(), [searchCompanies]);

  const onSuggestionSelect = (suggestion) => {
    selectionMadeRef.current = true;
    setSearchState({ suggestions: [], showSuggestions: false, loading: false });
    companiesService.getCompany(suggestion.id)
      .then(({ id: _excluded, ...fullData }) => companiesService.createCompany(fullData))
      .then((newCompany) => {
        if (!mountedRef.current || !newCompany?.id) return;
        handleChange(entry.fieldId, newCompany.name || '');
        savedCompanyNameRef.current = newCompany.name || '';
        // Commit the FULL record (not just newCompany.id) -- ReviewTab.js's `updateDeal`
        // case forwards it to state.voucher.updatedInfo so CompanyContactReview.js can use
        // it directly instead of re-fetching a company we already have in hand.
        commitChange(entry, newCompany);
        selectionMadeRef.current = false;
      })
      .catch(() => {
        selectionMadeRef.current = false;
      });
  };

  const onCompanyBlur = (nextName) => {
    if (selectionMadeRef.current) return;
    if (!nextName || nextName === savedCompanyNameRef.current) return;

    const savePromise = currentCompany?.id
      ? companiesService.createCompanyHelper(
        { ...currentCompany, name: savedCompanyNameRef.current },
        { ...currentCompany, name: nextName },
      )
      : companiesService.createCompany({
        ...defaultCompanyData,
        name: nextName,
      });

    Promise.resolve(savePromise).then((newCompany) => {
      if (!mountedRef.current || !newCompany?.id) return;
      handleChange(entry.fieldId, newCompany.name || nextName);
      savedCompanyNameRef.current = newCompany.name || nextName;
      commitChange(entry, newCompany);
    }).catch(() => {});
  };

  return (
    <div className="company-name-search-wrapper required-field-row__company-search-wrapper">
      <input
        type="text"
        name="name"
        className="required-field-row__input"
        aria-label={entry.label}
        value={value || ''}
        disabled={isSaving}
        onChange={(e) => {
          const nextValue = e.target.value;
          handleChange(entry.fieldId, nextValue);
          searchCompanies(nextValue);
        }}
        onBlur={(e) => {
          const nextValue = e.target.value;
          setTimeout(() => {
            if (mountedRef.current) closeSuggestions();
          }, 150);
          onCompanyBlur(nextValue);
        }}
      />
      {searchState.loading && <div className="company-name-search-loading-bar" />}
      {searchState.showSuggestions && searchState.suggestions.length > 0 && (
        <div className="company-name-search-dropdown">
          {searchState.suggestions.map(s => (
            <div
              key={s.id}
              className="company-name-search-item"
              onMouseDown={() => onSuggestionSelect(s)}
              role="option"
              aria-selected="false"
              tabIndex={-1}
            >
              <span className="company-name-search-item__name">{s.name}</span>
              {s.address && (
                <span className="company-name-search-item__address">
                  {[s.address.address, s.address.city, s.address.territory, s.address.postal, s.address.country]
                    .filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

ReviewCompanyNameSearchControl.propTypes = {
  entry: PropTypes.shape({
    fieldId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    editTarget: PropTypes.shape({
      currentCompany: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
  value: PropTypes.string,
  isSaving: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  commitChange: PropTypes.func.isRequired,
};

ReviewCompanyNameSearchControl.defaultProps = {
  value: '',
};

/**
 * Renders the SAME "Add Consideration" button (and dispatches the exact same
 * `createConsideration` payload) `Considerations.js` itself renders on the live Financials
 * tab -- used in place of the old read-only "View" link so a voucher can add a Consideration
 * row directly from this panel instead of navigating away. Registered via
 * `useRegisterAddRowAction` (see `RequiredFieldsContext.js`), so it's ALWAYS shown (matching
 * the live tab), independent of whether the list is currently empty -- `position_id` is
 * always `1` since this only ever appends a new row at the end of Financial tab's own list.
 */
const ConsiderationAddRow = ({ dealId, dealType }) => {
  const dispatch = useDispatch();
  return (
    <div className="required-field-row__add-buttons">
      <AddItem
        label="Add Consideration"
        onClick={() => dispatch(createConsideration({
          amount: null,
          commission_amount: null,
          consideration_amount: 0,
          consideration_type: dealType === 'lease' ? 'Monthly Rent' : 'Purchase Price',
          transaction: dealId,
          total_units: null,
          position_id: 1,
        }))}
      />
    </div>
  );
};

ConsiderationAddRow.propTypes = {
  dealId: PropTypes.number.isRequired,
  dealType: PropTypes.string.isRequired,
};

/**
 * Renders the SAME "Add Broker" / "Add Office" / "Add Pool Account" button (and dispatches
 * the exact same `createCommission` payload) `BrokerCommissions.js`/`OfficeCommissions.js`/
 * `PoolCommissions.js` each render on the live Commissions tab. Each is registered as its
 * OWN independent `useRegisterAddRowAction` entry in `CommissionsReview.js` (sharing a
 * `rowGroup` with that subtype's own rows), so it's ALWAYS shown directly below that
 * subtype's rows -- Broker rows then "Add Broker", Office rows then "Add Office", Pool rows
 * then "Add Pool Account" -- exactly mirroring the live tab's 3 independently-stacked
 * components, instead of one combined block of 3 buttons.
 */
const BrokerAddRow = ({ dealId }) => {
  const dispatch = useDispatch();
  return (
    <div className="required-field-row__add-buttons">
      <AddItem
        label="Add Broker"
        onClick={() => dispatch(createCommission({
          broker_is_lead: true, cbre_broker_type: 'employee', payment_group: 1, transaction: dealId,
        }))}
      />
    </div>
  );
};

BrokerAddRow.propTypes = {
  dealId: PropTypes.number.isRequired,
};

const OfficeAddRow = ({ dealId }) => {
  const dispatch = useDispatch();
  return (
    <div className="required-field-row__add-buttons">
      <AddItem
        label="Add Office"
        onClick={() => dispatch(createCommission({
          broker_is_lead: true, cbre_broker_type: 'office', payment_group: 1, transaction: dealId,
        }))}
      />
    </div>
  );
};

OfficeAddRow.propTypes = {
  dealId: PropTypes.number.isRequired,
};

const PoolAddRow = ({ dealId }) => {
  const dispatch = useDispatch();
  return (
    <div className="required-field-row__add-buttons">
      <AddItem
        label="Add Pool Account"
        onClick={() => dispatch(createCommission({
          broker_is_lead: true, cbre_broker_type: 'pool', payment_group: 1, transaction: dealId,
        }))}
      />
    </div>
  );
};

PoolAddRow.propTypes = {
  dealId: PropTypes.number.isRequired,
};

/**
 * Renders the SAME "Add Installment" button (and dispatches the exact same
 * `addInstallment` payload) `Installments.js` itself renders on the live Installments tab --
 * used in place of the old read-only "View" link. Registered via `useRegisterAddRowAction`
 * (see `RequiredFieldsContext.js`), so it's ALWAYS shown (matching the live tab), independent
 * of whether the list is currently empty.
 */
const InstallmentAddRow = ({ dealId }) => {
  const dispatch = useDispatch();
  return (
    <div className="required-field-row__add-buttons">
      <AddItem
        label="Add Installment"
        onClick={() => dispatch(addInstallment({
          transaction: dealId,
          installment_number: 1,
          contingencies: [],
        }))}
      />
    </div>
  );
};

InstallmentAddRow.propTypes = {
  dealId: PropTypes.number.isRequired,
};

/**
 * Renders a registered "custom row" entry (see `useRegisterRequiredRows` in
 * `RequiredFieldsContext.js`) using the EXACT SAME editable row component the live
 * Financials tab itself renders for that row -- so a Consideration, Commission,
 * Installment, or Allocation row that's missing a required field shows up here with
 * identical fields, styling, and redux dispatch behavior instead of a generic control.
 * `entry.rowProps` carries whatever extra scalar context (deal_type, voucherId, etc.)
 * that specific row component needs beyond its own `id`.
 */
const renderCustomRowContent = (entry) => {
  const rowProps = entry.rowProps || {};
  switch (entry.customRowType) {
    case 'consideration':
      return <ConsiderationItem id={entry.rowId} showValidation onDeleteReorder={noop} />;
    case 'additionalCommission':
      return <AdditionalCommissionItem id={entry.rowId} showValidation />;
    case 'brokerCommission':
      return <BrokerCommissionItem id={entry.rowId} showValidation {...rowProps} />;
    case 'officeCommission':
      return <OfficeCommissionItem id={entry.rowId} showValidation {...rowProps} />;
    case 'poolCommission':
      return <PoolCommissionItem id={entry.rowId} showValidation {...rowProps} />;
    case 'installment':
      return <InstallmentItem id={entry.rowId} showValidation />;
    // Each of these embeds the WHOLE live Allocations sub-section (header text, Yes/No
    // radio toggle, every row, and its own "Add X" button) verbatim -- same "reused
    // verbatim" approach as `billTo` below -- instead of one entry per row, so the panel
    // shows the exact same section context and controls as the live Financials tab.
    case 'outsideBrokersSection':
      return <OutsideBrokers showValidation {...rowProps} />;
    case 'taxAllocationsSection':
      return <TaxAllocations showValidation {...rowProps} />;
    case 'feeSharingSection':
      return <FeeSharing showValidation {...rowProps} />;
    case 'rebatesSection':
      return <Rebates showValidation {...rowProps} />;
    // The "Billed To" party picker isn't a per-row list -- it's the single real Bill To
    // tab itself (Tenant/Landlord/Other radio buttons + company/contact preview), reused
    // verbatim via its connected container so the panel's design/behavior is identical.
    case 'billTo':
      return <BillToTabContainer />;
    // "Add Row" action entries (see `useRegisterAddRowAction` in `RequiredFieldsContext.js`)
    // -- always-visible action buttons, positioned via `rowGroup`/`isAddButton` ordering
    // (see `orderSectionEntries`) directly after that subtype's own rows.
    case 'considerationAdd':
      return <ConsiderationAddRow {...rowProps} />;
    case 'brokerAdd':
      return <BrokerAddRow {...rowProps} />;
    case 'officeAdd':
      return <OfficeAddRow {...rowProps} />;
    case 'poolAdd':
      return <PoolAddRow {...rowProps} />;
    case 'installmentAdd':
      return <InstallmentAddRow {...rowProps} />;
    default:
      return null;
  }
};

// The scope tells embedded rows' edit metrics (see ReviewEditTrackingContext.js) which
// Review section/row they belong to.
const renderCustomRow = (entry) => {
  const content = renderCustomRowContent(entry);
  if (!content) return null;
  return (
    <ReviewEditRowScope rowType={entry.customRowType} rowId={entry.rowId} section={entry.section}>
      {content}
    </ReviewEditRowScope>
  );
};


// `ConsiderationItem`/`AdditionalCommissionItem` render only their own data cells -- on the
// live Financials tab, the column labels ("Consideration Type", "Period", "Rent($)", etc.)
// come from a header row `Considerations.js`/`AdditionalCommissions.js` render ONCE above
// the whole list, not from the item itself. Reusing their exact `table`/`table-header`/
// `table-cell` markup (styled by the already-globally-imported `considerations.scss`/
// `additional-commissions.scss`) reproduces that header here, identically.
const CUSTOM_ROW_TABLE_WRAPPER_CLASS = {
  consideration: 'considerations-table',
  additionalCommission: 'additional-commissions-table',
};

const renderCustomRowHeader = (rowType, rowProps = {}) => {
  switch (rowType) {
    case 'consideration':
      return (
        <div className="table-header considerations-header-row">
          <div className="table-cell">Consideration Type</div>
          <div className="table-cell">{rowProps.unitLabel || 'Period'}</div>
          <div className="table-cell">{rowProps.amountLabel ? `${rowProps.amountLabel}($)` : 'Rent($)'}</div>
          <div className="table-cell">Consideration</div>
          <div className="table-cell">Commission Rate</div>
          <div className="table-cell">Commission</div>
          <div className="table-cell" />
        </div>
      );
    case 'additionalCommission':
      return (
        <div className="table-header">
          <div className="table-cell">Commission Type</div>
          <div className="table-cell">Quantity</div>
          <div className="table-cell">Amount($)</div>
          <div className="table-cell">Additional Commission</div>
          <div className="table-cell" />
        </div>
      );
    default:
      return null;
  }
};

/**
 * Groups a section's entries into contiguous runs so `consideration`/`additionalCommission`
 * rows -- which need a single shared header rendered once above the whole run, inside the
 * SAME `.table.considerations-table`/`.table.additional-commissions-table` ancestor the live
 * tab uses (required for the header/data-cell column widths to line up) -- are wrapped
 * together, while every other row (which already labels itself, or isn't a custom row at
 * all) is left as its own standalone entry.
 */
const groupSectionEntries = (sectionEntries) => {
  const groups = [];
  sectionEntries.forEach((entry) => {
    const wrapperClass = CUSTOM_ROW_TABLE_WRAPPER_CLASS[entry.customRowType];
    const previousGroup = groups[groups.length - 1];
    if (wrapperClass && previousGroup && previousGroup.rowType === entry.customRowType) {
      previousGroup.entries.push(entry);
      return;
    }
    if (wrapperClass) {
      groups.push({ rowType: entry.customRowType, wrapperClass, entries: [entry] });
      return;
    }
    groups.push({ entries: [entry] });
  });
  return groups;
};

// Simple rows with one of these `controlType`s (a Yes/No radio group, the two-box Free Rent
// Years/Months pair, or a long-form Textarea) read awkwardly squeezed into a half-width
// column -- they render as their own full-width row instead of being paired up below.
const WIDE_CONTROL_TYPES = ['boolean', 'freeRent', 'textarea', 'percentDollar', 'leaseEscalationSchedule'];

// A group can share the two-up `.required-field-pairs` grid ONLY if it's a single plain
// field row (not the grouped Consideration/Additional-Commission `table` wrapper, not a
// standalone embedded Financials-tab row like Commission/Installment/Allocation/Bill To --
// these are wide, multi-field rows in their own right and need the panel's full width, not
// a half-width column -- and not one of the `WIDE_CONTROL_TYPES` above, and not a bare
// "you must add at least one X" message with no `editTarget` -- e.g. Commissions'
// `commission-list-required` was pairing up beside "Pursuit ID" instead of always
// rendering full-width directly below it).
const isPairEligible = (group) => {
  if (group.wrapperClass) return false;
  const [entry] = group.entries;
  if (entry.customRowType) return false;
  if (!entry.editTarget) return false;
  return !WIDE_CONTROL_TYPES.includes(entry.controlType);
};

/**
 * Further batches a section's `groupSectionEntries(...)` output into contiguous runs of
 * pair-eligible rows (wrapped together in their own `.required-field-pairs` two-column grid,
 * so simple field pairs like Lease Start/Expiration Date sit close together instead of
 * stretching across the panel's full, uncapped width) versus rows that need the full panel
 * width on their own line (rendered standalone, entirely outside that grid, so capping the
 * pairs grid's column width never squeezes them down too).
 */
const batchIntoRenderUnits = (sectionGroups) => {
  const units = [];
  sectionGroups.forEach((group) => {
    if (isPairEligible(group)) {
      const previousUnit = units[units.length - 1];
      if (previousUnit && previousUnit.type === 'pairs') {
        previousUnit.groups.push(group);
        return;
      }
      units.push({ type: 'pairs', groups: [group] });
      return;
    }
    units.push({ type: 'standalone', group });
  });
  return units;
};

/**
 * Top-of-page "Before you submit" required-fields panel (FR-9), grouped by originating
 * section and styled to match the approved `Review.dc.html` reference design. Reads
 * entries from the shared `RequiredFieldsContext` registry, which every `RequiredLabeledItem`
 * instance across the Review page's child components (`GettingStartedReview`,
 * `PropertyReview`, `CompDataReview`, `CompanyContactReview`, `BillToReview`, etc.)
 * populates client-side — there is no backend dependency for required-field detection.
 *
 * Each row renders the SAME control the field uses in its live voucher edit Tab
 * (`controlType`: a real calendar `Datepicker` for dates, the same `Select` dropdown for
 * dropdown fields, Yes/No radio buttons for booleans, a `Textarea` for long text, and a
 * plain text input otherwise) so editing here behaves identically to editing on the
 * field's own tab, dispatching through the same `onEditField` -> Redux action pipeline.
 */
/**
 * Small status badge used for the panel's overall header/footer state -- a red circle "!"
 * while required fields remain, a green circle check once everything is resolved, matching
 * the `@cbrebuild/blocks` Icon component's closest built-in glyphs ('danger'/'okay') for
 * neither of which render as a true circle, so those two states render the circle directly.
 * The 'warning' (only out-of-range/advisory values left) state instead matches the approved
 * `Review.dc.html` reference design's shield-check glyph, not a circle -- rendered as an
 * inline SVG since `@cbrebuild/blocks` has no shield/verified icon.
 */
const StatusIcon = ({ status }) => {
  if (status === 'warning') {
    return (
      <span className="required-fields-summary__status-icon required-fields-summary__status-icon--warning" aria-hidden="true">
        {/* Fills the full 20x20 `&__status-icon` box (instead of a smaller 16x16 glyph
            floating inside it with visible padding around it) so this reads as the exact
            same size as the enlarged 20px label warning icon next to each out-of-range
            field, instead of looking noticeably smaller/inconsistent beside it. */}
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="currentColor" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
          <path fill="#fff" d="M10.59 16.59L6.7 12.7l1.41-1.41 2.48 2.47 5.79-5.79 1.41 1.41z" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className={`required-fields-summary__status-icon required-fields-summary__status-icon--${status}`}
      aria-hidden="true"
    >
      {status === 'error' ? '!' : '✓'}
    </span>
  );
};

StatusIcon.propTypes = {
  status: PropTypes.oneOf(['error', 'warning', 'success']).isRequired,
};

/**
 * Footer banner content -- one message per outstanding category (missing required fields,
 * out-of-range warnings), shown TOGETHER when both exist at once, instead of the warning
 * message getting hidden behind the required-fields one. CONTINUE label/disabled-state still
 * keyed off the same 'error' > 'warning' > 'success' priority as `StatusIcon` -- required
 * fields still block Continue even once shown alongside a range warning.
 */
const getFooterMessages = (missingCount, rangeWarningCount, verificationRequired) => {
  const messages = [];
  // The shared verification decision (when given) decides; otherwise fall back to range warnings.
  const showVerification = verificationRequired === undefined ? rangeWarningCount > 0 : verificationRequired;
  if (missingCount > 0) {
    messages.push({
      status: 'error',
      title: `${missingCount} required field${missingCount === 1 ? ' still needs' : 's still need'} attention`,
      subtitle: 'These are critical to the comp and must be completed before you can continue.',
    });
  }
  if (showVerification) {
    messages.push({
      status: 'warning',
      title: rangeWarningCount > 0
        ? `${rangeWarningCount} value${rangeWarningCount === 1 ? '' : 's'} left outside typical ranges`
        : 'Broker verification needed',
      subtitle: 'Continue will start Voucher verification, where the broker confirms these values by email.',
    });
  }
  if (messages.length === 0) {
    messages.push({
      status: 'success',
      title: 'All checks passed',
      subtitle: 'No values need broker verification. You can continue straight to submit.',
    });
  }
  return messages;
};

const FOOTER_BUTTON_LABEL = {
  error: 'CONTINUE',
  warning: 'CONTINUE TO VERIFICATION',
  success: 'CONTINUE TO SUBMIT',
};

// Keep the panel's top-level section order aligned with voucher flow where Getting
// Started is always first, followed by lease-level and party/company sections.
const SECTION_DISPLAY_PRIORITY = [
  'Getting Started',
  'Lease Information',
  'Tenant Information',
  'Landlord Information',
];

const orderSectionNames = (unorderedSectionNames) => {
  const sectionSet = new Set(unorderedSectionNames);
  const prioritized = SECTION_DISPLAY_PRIORITY.filter(name => sectionSet.has(name));
  const remaining = unorderedSectionNames.filter(name => !SECTION_DISPLAY_PRIORITY.includes(name));
  return [...prioritized, ...remaining];
};

const SECTION_FIELD_PRIORITY = {
  // Without this, "Getting Started" falls back to `orderEntriesByRowGroup` (registration
  // order) -- and the 3 voucher-verification fields (Is this a fully executed lease? / Is
  // there a commission agreement? / Upload Commission Agreement) only register once their
  // async draft fetch resolves, landing at the end of the panel instead of next to Account
  // Client, and racing against each other for exact adjacency. This also guarantees "Client
  // Code - Description" always sits directly below "Account Client", and "Upload Commission
  // Agreement" always sits directly below "Is there a commission agreement?", with nothing
  // else able to land between either pair regardless of registration timing.
  'Getting Started': [
    'Reason for Early Invoice',
    'Lead Office',
    'Office Name',
    'Account Client',
    'Client Code - Description',
    'Is this a fully executed lease?',
    'Is there a commission agreement?',
    'Upload Commission Agreement',
  ],

  // Without this, "Lease Information" falls back to `orderEntriesByRowGroup` (registration-
  // order), and Property Subtype only ever registers once Property Type is picked (see
  // `notRequired={... || !selectedPropertyType}` in PropertyReview.js) -- landing at the end
  // of the panel instead of staying next to Property Type.
  'Lease Information': [
    'Lease Start Date',
    'Lease Expiration Date',
    'Lease Execution Date',
    'Lease Occupancy Date',
    'Rate Type',
    'Lease Transaction Type',
    'Address',
    'City',
    'State',
    'Zip',
    'Property Type',
    'Property Subtype',
    'Space Type',
    'Square Footage',
    'Parcel Size/Acres',
    'Floor',
    'Square Feet',
  ],
  'Tenant Information': [
    'Company Name',
    'Business Type / Use ID',
    'Address',
    'City',
    'State',
    'Zip',
    'GWS Client',
    'Parent Company',
    'Tenant Credit Rating',
    'First Name',
    'Last Name',
    'Phone #',
    'Email Address',
    'Job Title',
  ],
  'Landlord Information': [
    'Company Name',
    'Address',
    'City',
    'State',
    'Zip',
    'GWS Client',
    'Parent Company',
    'First Name',
    'Last Name',
    'Phone #',
    'Email Address',
    'Job Title',
  ],
  // Without this, "Escalation (Per Year)"/"Escalation Schedule" fall back to
  // `orderEntriesByRowGroup` (registration order) -- they only register once Fixed/Custom is
  // picked (CompDataReview.js's `rentEscalationTypeSelected` gate), well after every other
  // Comp Data field has already registered on mount, landing at the end of the panel instead
  // of directly below "Lease Escalation" (see `forceRegister={escalationDetailMissing}` there).
  'Comp Data': [
    'Tenant Represented By',
    'Tenant Represented By Agent',
    'Buyer Represented By',
    'Buyer Represented By Agent',
    'Landlord Represented By',
    'Landlord Represented By Agent',
    'Seller Represented By',
    'Seller Represented By Agent',
    'Base Rent ($/SF per year)',
    'Free Rent (Months)',
    'Lease Escalation',
    'Escalation (Per Year)',
    'Lease Escalation Breakdown',
    'Tenant Improvement Allowance ($/SF)',
    'Tenant Improvements Detail',
    'Operating Expenses ($/SF)',
    'Electric included in Oper Exp.',
    'Electric Expense ($/SF)',
  ],
};

// Fixed display order for the "sub-groups" within a single Financials-list section (e.g.
// Broker rows/button before Office rows/button before Pool rows/button in "Commissions") --
// see `ConsiderationReview.js`/`CommissionsReview.js`/`InstallmentsReview.js`'s
// `useRegisterRequiredRows`/`useRegisterAddRowAction` calls, which tag their entries with a
// matching `rowGroup`.
const ROW_GROUP_ORDER = {
  consideration: 0,
  broker: 0,
  office: 1,
  pool: 2,
  installment: 0,
};

/**
 * Orders a section's entries so each `rowGroup` (e.g. "broker") stays together as a block,
 * in `ROW_GROUP_ORDER` order, with that group's own rows (registered via
 * `useRegisterRequiredRows`) before its `isAddButton` entry (registered via
 * `useRegisterAddRowAction`) -- e.g. Broker rows then "Add Broker", Office rows then
 * "Add Office", Pool rows then "Add Pool Account". Entries with no `rowGroup` at all (e.g.
 * the section's "You must add at least one X" message, which isn't part of any subtype's
 * block) always sort first, keeping their original relative order (a stable sort, per the
 * ES2019+ spec, leaves equally-ranked entries in their original order) -- if NO entry in the
 * section declares a `rowGroup`, the array is returned unchanged.
 */
const orderEntriesByRowGroup = (unorderedEntries) => {
  if (!unorderedEntries.some(entry => entry.rowGroup)) return unorderedEntries;
  return [...unorderedEntries].sort((left, right) => {
    const leftOrder = left.rowGroup ? (ROW_GROUP_ORDER[left.rowGroup] ?? 0) : -1;
    const rightOrder = right.rowGroup ? (ROW_GROUP_ORDER[right.rowGroup] ?? 0) : -1;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return (left.isAddButton ? 1 : 0) - (right.isAddButton ? 1 : 0);
  });
};

const orderSectionEntries = (sectionName, unorderedEntries) => {
  const priorities = SECTION_FIELD_PRIORITY[sectionName];
  if (priorities) {
    const priorityIndex = new Map(priorities.map((label, index) => [label, index]));
    return [...unorderedEntries].sort((left, right) => {
      const leftIndex = priorityIndex.has(left.label) ? priorityIndex.get(left.label) : Number.MAX_SAFE_INTEGER;
      const rightIndex = priorityIndex.has(right.label) ? priorityIndex.get(right.label) : Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    });
  }
  return orderEntriesByRowGroup(unorderedEntries);
};

const splitEntriesBySubSection = (orderedEntries) => {
  const DEFAULT_KEY = '__default__';
  const groups = orderedEntries.reduce((acc, entry) => {
    const subSectionName = entry.subSection || DEFAULT_KEY;
    if (!acc[subSectionName]) {
      acc[subSectionName] = {
        title: subSectionName === DEFAULT_KEY ? null : subSectionName,
        entries: [],
      };
    }
    acc[subSectionName].entries.push(entry);
    return acc;
  }, {});
  return Object.values(groups);
};

const RequiredFieldsSummary = ({
  onEditField, onContinue, verificationRequired, verificationLoading,
}) => {
  const registry = useRequiredFieldsRegistry();
  const [draftValues, setDraftValues] = useState({});
  // Tracks whether a client-code field's CURRENT draft came from a real autocomplete
  // selection object (valid) vs free-typed text (invalid for this field type).
  const [clientCodeSelectionByFieldId, setClientCodeSelectionByFieldId] = useState({});
  const [naicsSelectionByFieldId, setNaicsSelectionByFieldId] = useState({});
  // Tracks fieldIds with an in-flight commit, so the control can show the same
  // disabled/"Saving" state the live voucher edit Tabs show while a Redux dispatch is
  // pending -- `onEditField` (wired up in ReviewTab.js) returns the dispatch's promise.
  const [savingFieldIds, setSavingFieldIds] = useState({});

  const entries = Object.entries(registry?.entries || {});
  // Once the panel has shown at least one entry, keep it (and its footer/CONTINUE button)
  // rendered even after every entry resolves and is unregistered -- so the user sees the
  // banner turn green ("All checks passed") and gets a CONTINUE TO SUBMIT button, instead
  // of the whole panel abruptly vanishing. A deal that never had anything to flag (nothing
  // was ever registered) still renders nothing, matching the "renders nothing when no
  // fields are registered" behavior.
  const [hasEverHadEntries, setHasEverHadEntries] = useState(entries.length > 0);
  useEffect(() => {
    if (entries.length > 0 && !hasEverHadEntries) setHasEverHadEntries(true);
  }, [entries.length, hasEverHadEntries]);

  const groups = entries.reduce((acc, [fieldId, entry]) => {
    const sectionName = entry.section || 'Other';
    if (!acc[sectionName]) acc[sectionName] = [];
    acc[sectionName].push({ fieldId, ...entry });
    return acc;
  }, {});
  const orderedSectionNames = orderSectionNames(Object.keys(groups));

  // Entries stay registered even once resolved (see RequiredLabeledItem.js/
  // RequiredFieldsContext.js's `complete` flag) so the panel can show a green checkmark
  // instead of the row disappearing -- so the footer's counts (and therefore the CONTINUE
  // button gating) must only consider entries that are STILL incomplete. "Add Row" action
  // entries (`isAddButton`, e.g. "Add Broker"/"Add Consideration") are just navigation
  // buttons, not their own requirement -- the actual "you must add at least one X" gap they
  // address is already counted via its own separate `useRegisterRequiredField` entry, so
  // counting the button too would inflate one missing requirement into several (2026-08-04:
  // reported as "8 required fields" for what visibly showed only 3 messages). Similarly,
  // `excludeFromCount` entries (the same "you must add at least one X" message, once a row
  // already exists) are excluded too -- once a row has been added, ITS OWN incomplete-row
  // entry represents the gap instead, so counting the still-visible message on top of that
  // row would double-count the same underlying requirement (2026-08-04 follow-up: clicking
  // "Add Consideration" for the first time bumped the count even though nothing NEW needed
  // resolving -- only adding an ADDITIONAL incomplete row should increase the count).
  const incompleteEntries = entries.filter(([, entry]) => !entry.complete && !entry.isAddButton && !entry.excludeFromCount);
  const missingCount = incompleteEntries.filter(([, entry]) => !entry.isRangeWarning).length;
  const rangeWarningCount = incompleteEntries.length - missingCount;
  // 'error' (required fields missing) takes priority over 'warning' (only out-of-range
  // values left) over 'success' (nothing left to resolve) -- see `getFooterCopy` above.
  const showVerification = verificationRequired === undefined ? rangeWarningCount > 0 : verificationRequired;
  const footerStatus = (() => {
    if (missingCount > 0) return 'error';
    if (showVerification) return 'warning';
    return 'success';
  })();
  const footerCopy = {
    messages: getFooterMessages(missingCount, rangeWarningCount, verificationRequired),
    buttonLabel: FOOTER_BUTTON_LABEL[footerStatus],
  };

  const handleChange = (fieldId, value) => {
    setDraftValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const commitChange = (entry, value, { allowEmpty = false } = {}) => {
    if (value === undefined || (!allowEmpty && value === '')) return;
    const result = onEditField(entry, value);
    // `handleEditField` in ReviewTab.js returns the `dispatch(updateXxx(...))` promise so
    // this panel can mirror it, disabling the control until the request settles -- the
    // same round-trip every live voucher edit Tab already goes through.
    if (result && typeof result.then === 'function') {
      setSavingFieldIds(prev => ({ ...prev, [entry.fieldId]: true }));
      Promise.resolve(result).finally(() => {
        setSavingFieldIds((prev) => {
          const next = { ...prev };
          delete next[entry.fieldId];
          return next;
        });
      });
    }
  };

  const handleBlur = (entry) => {
    // `allowEmpty: true` so clearing a field out to '' still commits (and re-runs required
    // validation) instead of silently no-op'ing -- matches the live voucher flow's own blur
    // handlers (e.g. useCommercialPropertyState's onSpaceBlur), which always PATCH on blur
    // regardless of whether the new value is blank.
    commitChange(entry, draftValues[entry.fieldId], { allowEmpty: true });
  };

  // Mirrors VoucherContactForm's suppressGivenNameBlurPatchRef/suppressSurnameBlurPatchRef --
  // selecting a contact search result already commits the full contact object immediately,
  // so the blur that follows the selection must not ALSO fire the plain single-field patch
  // `handleBlur` would otherwise send, which was causing 2 PATCH requests per selection.
  const suppressContactBlurCommitRef = useRef({});

  const getDraftValue = useCallback((fieldId, fallbackValue = '') => {
    if (!fieldId) return fallbackValue;
    return draftValues[fieldId] !== undefined ? draftValues[fieldId] : fallbackValue;
  }, [draftValues]);

  if (!hasEverHadEntries) return null;

  // Backend rejects `space_total_square_feet` (and other numeric fields validated the same
  // way) unless it's a real float/int/Decimal, not a string -- mirrors
  // useCommercialPropertyState.js's `onSpaceBlur`, which does the same `parseInt(value, 10)`
  // before dispatching.
  const handleNumberBlur = (entry) => {
    const raw = draftValues[entry.fieldId];
    if (raw === undefined) return;
    // Clearing the field commits `null` (not '') so the required check re-fails correctly.
    if (raw === '') {
      commitChange(entry, null, { allowEmpty: true });
      return;
    }
    const parsed = parseInt(raw, 10);
    commitChange(entry, Number.isNaN(parsed) ? raw : parsed, { allowEmpty: true });
  };

  // For decimal fields (Base Rent, TIA, Operating Expenses, Electric Expense $/SF) -- 'number'
  // above is integer-only (Square Footage/Floor/Square Feet), so a separate blur/sanitizer
  // pair is needed to allow a decimal point through without breaking those integer fields.
  const handleCurrencyBlur = (entry) => {
    const raw = draftValues[entry.fieldId];
    if (raw === undefined) return;
    if (raw === '') {
      commitChange(entry, null, { allowEmpty: true });
      return;
    }
    const parsed = parseFloat(raw);
    commitChange(entry, Number.isNaN(parsed) ? raw : parsed, { allowEmpty: true });
  };

  // These render as type="text" (not type="number") to avoid the browser's native
  // scroll-to-change-value behavior on focused number inputs -- same reason nucleus's own
  // NumberInput (used across the live voucher flow) is type="text" under the hood. Since
  // that drops the browser's native digit-only filtering, filter keystrokes here instead.
  const sanitizeIntegerInput = raw => raw.replace(/[^\d]/g, '');
  // `maxDecimals` mirrors the live voucher flow's own nucleus NumberInput `precision` prop
  // (e.g. LeaseComp.js passes precision={2} for base_rent/TIA/operating/electric expense).
  const sanitizeDecimalInput = (raw, maxDecimals) => {
    const cleaned = raw.replace(/[^\d.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot === -1) return cleaned;
    let decimals = cleaned.slice(firstDot + 1).replace(/\./g, '');
    if (maxDecimals !== undefined) decimals = decimals.slice(0, maxDecimals);
    return `${cleaned.slice(0, firstDot)}.${decimals}`;
  };

  const renderControl = (entry) => {
    // Seed from the field's actual current value (`entry.value`, carried through from
    // RequiredLabeledItem/BillToReview) until the user edits it in THIS panel -- otherwise
    // a field that was already filled in (e.g. on page load/refresh, or switching tabs)
    // would render blank here even though it has a real, saved value elsewhere.
    const value = draftValues[entry.fieldId] !== undefined ? draftValues[entry.fieldId] : entry.value;
    const isSaving = !!savingFieldIds[entry.fieldId];
    switch (entry.controlType) {
      case 'boolean':
        return (
          <RadioGroup
            name={entry.fieldId}
            className="required-field-row__radio"
            selectedValue={value ?? ''}
            disabled={isSaving}
            onChange={(e) => {
              const stringValue = e.target.value;
              handleChange(entry.fieldId, stringValue);
              // Dispatch the real boolean (matching e.g. GettingStartedTab.js's own
              // `handleAccountManagementClientChange`), not the radio's raw "true"/"false"
              // string -- code elsewhere strictly compares these fields with `=== true`.
              commitChange(entry, stringValue === 'true');
            }}
            orientation="horizontal"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes</RadioButton>
          </RadioGroup>
        );
      case 'commissionAttachment':
        return (
          <CommissionAttachmentSelector
            dealId={entry.editTarget?.targetId}
            selectedIds={Array.isArray(value) ? value : []}
            uploadTitle={entry.label}
            // Red border while the Yes answer still has no document; "No" never registers as incomplete.
            isRequired={!entry.complete}
            // Same as Getting Started: the agreement is sent along with the voucher.
            isVoucherAttachment
            onChange={(attachmentIds) => {
              handleChange(entry.fieldId, attachmentIds);
              commitChange(entry, attachmentIds, { allowEmpty: true });
            }}
          />
        );
      case 'date':
        return (
          <Datepicker
            date={value ?? ''}
            disabled={isSaving}
            onDateChange={(date) => {
              handleChange(entry.fieldId, date);
              // Datepicker calls back with '' when cleared -- the API rejects '' for date
              // fields ("Date has wrong format"), so send null instead, and `allowEmpty:
              // true` so the commit still fires (and re-triggers required validation)
              // instead of no-op'ing.
              commitChange(entry, date || null, { allowEmpty: true });
            }}
          />
        );
      case 'select': {
        // `Select` only resolves a display label from `options` when `defaultOption` is the
        // matching OPTION OBJECT, not a bare value string -- passing the raw value directly
        // (e.g. Lease Escalation's 'true'/'false') rendered that raw value verbatim ("True")
        // instead of its `display_name` ("Custom"). Falls back to the raw value/'Select' for
        // values that aren't actually in this field's options list.
        const selectedOption = (entry.options || []).find(opt => opt.value === value);
        return (
          <Select
            className="required-field-row__input"
            defaultOption={selectedOption || value || 'Select'}
            options={entry.options || []}
            disabled={isSaving}
            // Select only reads `defaultOption` on mount; `onClear` makes it follow later value
            // changes that don't come from this dropdown (e.g. a company/address search filling State).
            onClear
            onChange={(option) => {
              // Mirror the live Tabs' own dropdown handlers (e.g. handleLeadOffice,
              // handleAgreementChange), which skip the unselected "Select" placeholder
              // option instead of persisting it as a real value.
              if (option.value === 'Select') return;
              const commitValue = option.commitValue !== undefined
                ? option.commitValue
                : option.value;
              handleChange(entry.fieldId, option.value);
              if (entry.fieldId.endsWith('-property-type') && option.linkedSubtype !== undefined) {
                const subtypeFieldId = entry.fieldId.replace('-property-type', '-property-subtype');
                handleChange(subtypeFieldId, option.linkedSubtype);
              }
              commitChange(entry, commitValue, { allowEmpty: true });
            }}
          />
        );
      }
      case 'number':
        return (
          <input
            type="text"
            inputMode="numeric"
            className="required-field-row__input"
            aria-label={entry.label}
            value={value ?? ''}
            disabled={isSaving}
            onChange={e => handleChange(entry.fieldId, sanitizeIntegerInput(e.target.value))}
            onBlur={() => handleNumberBlur(entry)}
          />
        );
      case 'currency':
        return (
          <input
            type="text"
            inputMode="decimal"
            className="required-field-row__input"
            aria-label={entry.label}
            value={value ?? ''}
            disabled={isSaving}
            onChange={e => handleChange(entry.fieldId, sanitizeDecimalInput(e.target.value, 2))}
            onBlur={() => handleCurrencyBlur(entry)}
          />
        );
      case 'textarea':
        return (
          <Textarea
            className="required-field-row__input"
            aria-label={entry.label}
            value={value ?? ''}
            disabled={isSaving}
            onChange={e => handleChange(entry.fieldId, e.target.value)}
            onBlur={() => handleBlur(entry)}
          />
        );
      // Plain-text ZIP field (CompanyContactReview.js's per-party Zip row, and
      // PropertyReview.js's Zip row for non-lease deals) -- validates the SAME 5-digit/ZIP+4
      // format the live voucher flow's AddressForm.js checks (`isValidPostal`), rejecting the
      // commit (no PATCH call) while the typed value fails that format, matching
      // useCompanyState.js's/useCommercialPropertyState.js's `onCompanyBlur`/`onPropertyBlur`
      // fix for the same condition. Its own inline error message is shown here (rather than
      // through the generic `renderFieldMessage` below, which only reflects the field's last
      // SAVED value via `entry.complete`/`entry.requiredMessage`) so the message appears in
      // real time as the user types, before ever blurring/committing -- see the `renderGroup`
      // exclusion list below (mirrors how `clientCode`/`naicsSearch` already show their own
      // inline error instead of the generic one).
      case 'zip': {
        const zipValue = value ?? '';
        const zipFormatInvalid = !!zipValue && !isValidPostal(zipValue, 'United States');
        return (
          <>
            <input
              type="text"
              className={`required-field-row__input${zipFormatInvalid ? ' required-field-row__input--error' : ''}`}
              aria-label={entry.label}
              value={zipValue}
              disabled={isSaving}
              onChange={e => handleChange(entry.fieldId, e.target.value)}
              onBlur={() => {
                if (zipFormatInvalid) return;
                handleBlur(entry);
              }}
            />
            {zipFormatInvalid && <span className="error-message">{ZIP_FORMAT_MESSAGE}</span>}
          </>
        );
      }
      // Renders the SAME flag/country-code phone input (IntlTelInput-backed) the live
      // voucher flow's Point of Contact form uses, instead of a plain text input --
      // matches VoucherContactForm.js's `phoneNumberOnChange`, which commits on every
      // change/blur as long as the number is valid.
      case 'phone':
        return (
          <PhoneInput
            hideLabel
            value={value ?? ''}
            onChange={(phoneValue, isValid) => {
              handleChange(entry.fieldId, phoneValue);
              if (isValid) commitChange(entry, phoneValue, { allowEmpty: true });
            }}
            onBlur={(phoneValue, isValid) => {
              handleChange(entry.fieldId, phoneValue);
              if (isValid) commitChange(entry, phoneValue, { allowEmpty: true });
            }}
          />
        );
      // Renders the SAME search-and-select autocomplete the live GettingStartedTab uses for
      // "Client Code - Description" (instead of a plain text input, or the read-only "View"
      // link this field used to fall back to before it had an `editTarget`) -- selecting a
      // result commits BOTH `account_code`/`account_description` together (see
      // ReviewTab.js's `handleEditField` 'updateDeal' case, which special-cases
      // `fieldPath === 'account_code_description'` to split this single committed value back
      // into the two real deal fields). Its own `errorMessage` below the input already
      // matches the live tab's wording, so the generic `renderFieldMessage` is skipped for
      // this controlType below (see the row layout) to avoid showing that text twice.
      case 'clientCode': {
        // `ClientCodeAutocomplete` treats any non-empty `errorMessage` as invalid, so this
        // must be conditional (not static): once the user selects a real result object
        // (or the row is already complete from persisted redux state), clear the error.
        // Keep showing it while the value is unresolved (missing or free-typed text that
        // wasn't picked from search results).
        const clientCodeSelectionValid = !!clientCodeSelectionByFieldId[entry.fieldId];
        const showClientCodeError = !entry.complete && !clientCodeSelectionValid;
        return (
          <ClientCodeAutocomplete
            initSearchTerm={value || ''}
            placeholder="Type to filter client codes"
            errorMessage={showClientCodeError ? 'You must select Client Code - Description from search results' : ''}
            onChange={(category) => {
              if (!category || typeof category === 'string') {
                setClientCodeSelectionByFieldId(prev => ({ ...prev, [entry.fieldId]: false }));
                handleChange(entry.fieldId, category || '');
                return;
              }
              setClientCodeSelectionByFieldId(prev => ({ ...prev, [entry.fieldId]: true }));
              handleChange(entry.fieldId, `${category.code} - ${category.description}`);
              commitChange(entry, { code: category.code, description: category.description });
            }}
          />
        );
      }
      case 'naicsSearch': {
        const naicsSelectionValid = !!naicsSelectionByFieldId[entry.fieldId];
        const showNaicsError = !entry.complete && !naicsSelectionValid;
        return (
          <NaicsAutocomplete
            initSearchTerm={value || ''}
            placeholder="Type to filter business categories"
            errorMessage={showNaicsError ? 'You must select Business Type / Use ID from search results' : ''}
            onChange={(naics) => {
              if (naics?.naics_code) {
                setNaicsSelectionByFieldId(prev => ({ ...prev, [entry.fieldId]: true }));
                handleChange(entry.fieldId, naics.naics_description);
                commitChange(entry, {
                  voucher_naics_code: naics.naics_code,
                  voucher_naics_category: naics.naics_description,
                });
              } else if (naics === '') {
                setNaicsSelectionByFieldId(prev => ({ ...prev, [entry.fieldId]: false }));
                handleChange(entry.fieldId, '');
                commitChange(entry, {
                  voucher_naics_code: null,
                  voucher_naics_category: null,
                });
              } else {
                setNaicsSelectionByFieldId(prev => ({ ...prev, [entry.fieldId]: false }));
                handleChange(entry.fieldId, naics || '');
              }
            }}
          />
        );
      }
      case 'companySearch':
        return (
          <ReviewCompanyNameSearchControl
            entry={entry}
            value={value ?? ''}
            isSaving={isSaving}
            handleChange={handleChange}
            commitChange={commitChange}
          />
        );
      // Renders the SAME contact search-and-select autocomplete the live voucher flow's
      // Point of Contact fields use (VoucherContactForm.js / file-upload lease-details
      // sections) instead of a plain text input. Typing free-text just commits that text to
      // THIS field on blur (same as a plain text field), but selecting an existing contact
      // from the results commits the FULL contact object -- ReviewTab.js's `handleEditField`
      // 'updateDealContact' case detects that and copies name/phone/email/job title onto the
      // current contact record all at once, matching VoucherContactForm's `nameOnChange`.
      case 'contactSearch':
        return (
          <ContactAutocomplete
            initSearchTerm={value || ''}
            placeholder="Search Contacts"
            onChange={(input) => {
              if (input && typeof input === 'object') {
                handleChange(entry.fieldId, input[entry.editTarget?.fieldPath] || '');
                suppressContactBlurCommitRef.current[entry.fieldId] = true;
                commitChange(entry, input);
              } else {
                handleChange(entry.fieldId, input || '');
              }
            }}
            onBlur={() => {
              if (suppressContactBlurCommitRef.current[entry.fieldId]) {
                suppressContactBlurCommitRef.current[entry.fieldId] = false;
                return;
              }
              handleBlur(entry);
            }}
          />
        );
      case 'propertyAddressSearch':
        return (
          <ReviewPropertyAddressSearchControl
            entry={entry}
            value={value ?? ''}
            isSaving={isSaving}
            handleChange={handleChange}
            commitChange={commitChange}
            getDraftValue={getDraftValue}
          />
        );
      // Matches the live voucher flow's own Free Rent design (LeaseComp.js / the file-upload
      // lease abstraction's `rent-and-escalations.js`): two separate Years/Months inputs, not
      // one. The underlying field (`compData.free_rent`) always stores/sends a single TOTAL
      // MONTHS number to the API -- `value` here is that total, split for display via
      // `convertFreeRentMonthsToYearsAndMonths`, and each box's `onChange` recombines the OTHER
      // box's current part with its own new part back into a total months figure via the same
      // `getUpdatedFreeRentTotalMonths` helper the live flow uses, so both places share one
      // source of truth for the years/months <-> total-months conversion.
      case 'freeRent': {
        const parts = convertFreeRentMonthsToYearsAndMonths(value);
        const handlePartChange = (part, partValue) => {
          const totalMonths = getUpdatedFreeRentTotalMonths({
            currentTotalMonths: value,
            part,
            value: partValue,
          });
          handleChange(entry.fieldId, totalMonths);
        };
        return (
          <div className="required-field-row__free-rent">
            <input
              type="text"
              inputMode="numeric"
              className="required-field-row__input required-field-row__free-rent-input"
              aria-label={`${entry.label} - Years`}
              value={parts.years}
              disabled={isSaving}
              onChange={e => handlePartChange('years', sanitizeIntegerInput(e.target.value))}
              onBlur={() => handleBlur(entry)}
            />
            <span className="required-field-row__free-rent-unit">Years</span>
            <input
              type="text"
              inputMode="numeric"
              className="required-field-row__input required-field-row__free-rent-input"
              aria-label={`${entry.label} - Months`}
              value={parts.months}
              disabled={isSaving}
              onChange={e => handlePartChange('months', sanitizeIntegerInput(e.target.value))}
              onBlur={() => handleBlur(entry)}
            />
            <span className="required-field-row__free-rent-unit">Months</span>
          </div>
        );
      }
      case 'percentDollar': {
        const isPercentDraftKey = `${entry.fieldId}__isPercent`;
        const percentDraftKey = `${entry.fieldId}__percent`;
        const dollarDraftKey = `${entry.fieldId}__dollar`;
        const initialIsPercent = entry.editTarget?.isPercent === true || String(entry.editTarget?.isPercent).toLowerCase() === 'true';
        const isPercent = draftValues[isPercentDraftKey] !== undefined
          ? draftValues[isPercentDraftKey]
          : initialIsPercent;
        const percentValue = draftValues[percentDraftKey] !== undefined
          ? draftValues[percentDraftKey]
          : (entry.editTarget?.percentValue ?? '');
        const dollarValue = draftValues[dollarDraftKey] !== undefined
          ? draftValues[dollarDraftKey]
          : (entry.editTarget?.dollarValue ?? '');
        const activeValue = isPercent ? percentValue : dollarValue;

        const handleTypeChange = (option) => {
          const nextIsPercent = option.value === '%';
          handleChange(isPercentDraftKey, nextIsPercent);
          commitChange(entry, { rent_escalation_is_percent: nextIsPercent }, { allowEmpty: true });
        };

        const handleValueChange = (nextValue) => {
          handleChange(isPercent ? percentDraftKey : dollarDraftKey, nextValue);
          handleChange(entry.fieldId, nextValue);
        };

        const handlePercentDollarBlur = () => {
          if (activeValue === undefined) return;
          // Clearing the active box commits null for it (not '') so the required check
          // re-fails correctly, instead of silently skipping the commit.
          if (activeValue === '') {
            commitChange(entry, {
              rent_escalation_is_percent: isPercent,
              rent_escalation_percent: isPercent ? null : 0,
              rent_escalation_dollar: isPercent ? 0 : null,
            }, { allowEmpty: true });
            return;
          }
          const parsed = parseFloat(activeValue);
          if (Number.isNaN(parsed)) return;
          if (isPercent) {
            commitChange(entry, {
              rent_escalation_is_percent: true,
              rent_escalation_percent: parsed,
              rent_escalation_dollar: 0,
            }, { allowEmpty: true });
            return;
          }
          commitChange(entry, {
            rent_escalation_is_percent: false,
            rent_escalation_dollar: parsed,
            rent_escalation_percent: 0,
          }, { allowEmpty: true });
        };

        return (
          <div className="required-field-row__percent-dollar">
            <Select
              className="required-field-row__percent-dollar-select"
              defaultOption={isPercent ? '%' : '$'}
              options={[
                { value: '$', display_name: '$' },
                { value: '%', display_name: '%' },
              ]}
              disabled={isSaving}
              onChange={handleTypeChange}
            />
            <input
              type="text"
              inputMode="decimal"
              className="required-field-row__input required-field-row__percent-dollar-input"
              aria-label={entry.label}
              value={activeValue ?? ''}
              disabled={isSaving}
              onChange={e => handleValueChange(sanitizeDecimalInput(e.target.value))}
              onBlur={handlePercentDollarBlur}
            />
          </div>
        );
      }
      // Custom Lease Escalation schedule (`compData.rent_escalation_custom`) -- repeatable
      // Rent $/% + Year/Month rows with Add/remove, matching the live voucher flow's own
      // "Lease Escalation Breakdown" editor (LeaseCompAbstractionTab.js's `FileUploadLeaseComp`
      // -> `rent-and-escalations.js` -> `customLeaseEscalationField.js`). Reuses this SAME
      // panel's own `percentDollar` (Select + input) and `freeRent` (Years/Months boxes via
      // `convertFreeRentMonthsToYearsAndMonths`/`getUpdatedFreeRentTotalMonths`) building
      // blocks per row, plus the `AddItem`/`IconButton` components the live editor uses,
      // instead of reinventing new markup -- the live editor's own components are tied to
      // `LeaseAbstractionContext` and can't be dropped in here directly.
      case 'leaseEscalationSchedule': {
        const rows = Array.isArray(value) && value.length > 0
          ? value
          : [{ months: '', rent_dollar: '', rent_percent: '', is_percent: false }];
        const isBlank = v => v === null || v === undefined || v === '';

        // Matches `customLeaseEscalationField.js`'s own `normalizeCustomEscalationPayload` --
        // the API rejects non-numeric/string values for these fields.
        const normalizeRows = rowsToNormalize => rowsToNormalize.map(row => ({
          months: parseInt(row.months, 10) || 0,
          rent_dollar: Number(row.rent_dollar) || 0,
          rent_percent: Number(row.rent_percent) || 0,
          is_percent: row.is_percent === true || String(row.is_percent).toLowerCase() === 'true',
        }));
        const commitRows = nextRows => commitChange(entry, normalizeRows(nextRows), { allowEmpty: true });
        const updateRow = (index, patch) => {
          const nextRows = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
          handleChange(entry.fieldId, nextRows);
          return nextRows;
        };

        return (
          <div className="required-field-row__escalation-schedule">
            {rows.map((row, index) => {
              const isPercent = row.is_percent === true || String(row.is_percent).toLowerCase() === 'true';
              const activeValue = isPercent ? row.rent_percent : row.rent_dollar;
              const rentMissing = isBlank(row.rent_dollar) && isBlank(row.rent_percent);
              const monthsMissing = isBlank(row.months);
              const parts = convertFreeRentMonthsToYearsAndMonths(monthsMissing ? '' : row.months);
              return (
                <div className="required-field-row__escalation-schedule-row" key={index}>
                  <div className="required-field-row__escalation-schedule-field">
                    <span className="required-field-row__escalation-schedule-label">{`Rent ${index + 1}`}</span>
                    <div className="required-field-row__percent-dollar">
                      <Select
                        className="required-field-row__percent-dollar-select"
                        defaultOption={isPercent ? '%' : '$'}
                        options={[
                          { value: '$', display_name: '$' },
                          { value: '%', display_name: '%' },
                        ]}
                        disabled={isSaving}
                        onChange={(option) => {
                          commitRows(updateRow(index, { is_percent: option.value === '%' }));
                        }}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="required-field-row__input required-field-row__percent-dollar-input"
                        aria-label={`Rent ${index + 1}`}
                        value={activeValue ?? ''}
                        disabled={isSaving}
                        onChange={(e) => {
                          const sanitized = sanitizeDecimalInput(e.target.value);
                          updateRow(index, isPercent ? { rent_percent: sanitized } : { rent_dollar: sanitized });
                        }}
                        onBlur={() => commitRows(rows)}
                      />
                    </div>
                    {rentMissing && <span className="error-message">Required</span>}
                  </div>
                  <div className="required-field-row__escalation-schedule-field">
                    <span className="required-field-row__escalation-schedule-label">Year</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="required-field-row__input required-field-row__free-rent-input"
                      aria-label={`Rent ${index + 1} - Years`}
                      value={parts.years}
                      disabled={isSaving}
                      onChange={(e) => {
                        const totalMonths = getUpdatedFreeRentTotalMonths({
                          currentTotalMonths: monthsMissing ? 0 : row.months,
                          part: 'years',
                          value: sanitizeIntegerInput(e.target.value),
                        });
                        updateRow(index, { months: totalMonths });
                      }}
                      onBlur={() => commitRows(rows)}
                    />
                    {monthsMissing && <span className="error-message">Year is required</span>}
                  </div>
                  <div className="required-field-row__escalation-schedule-field">
                    <span className="required-field-row__escalation-schedule-label">Month</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="required-field-row__input required-field-row__free-rent-input"
                      aria-label={`Rent ${index + 1} - Months`}
                      value={parts.months}
                      disabled={isSaving}
                      onChange={(e) => {
                        const totalMonths = getUpdatedFreeRentTotalMonths({
                          currentTotalMonths: monthsMissing ? 0 : row.months,
                          part: 'months',
                          value: sanitizeIntegerInput(e.target.value),
                        });
                        updateRow(index, { months: totalMonths });
                      }}
                      onBlur={() => commitRows(rows)}
                    />
                    {monthsMissing && <span className="error-message">Month is required</span>}
                  </div>
                  <IconButton
                    className="required-field-row__escalation-schedule-remove"
                    iconName="close-circle"
                    variant="basic"
                    disabled={isSaving || rows.length === 1}
                    onClick={() => {
                      const nextRows = rows.filter((_, i) => i !== index);
                      commitRows(nextRows.length > 0
                        ? nextRows
                        : [{ months: '', rent_dollar: '', rent_percent: '', is_percent: false }]);
                    }}
                  />
                </div>
              );
            })}
            <AddItem
              label="Add Rent"
              onClick={() => commitRows([...rows, { months: '', rent_dollar: '', rent_percent: '', is_percent: false }])}
            />
          </div>
        );
      }
      default:
        return (
          <input
            type="text"
            className="required-field-row__input"
            aria-label={entry.label}
            value={value ?? ''}
            disabled={isSaving}
            onChange={e => handleChange(entry.fieldId, e.target.value)}
            onBlur={() => handleBlur(entry)}
          />
        );
    }
  };

  // Shared by both the editable-control and read-only row layouts below -- avoids a nested
  // ternary (savingFieldIds check nested inside an isRangeWarning check) by factoring the
  // isRangeWarning-vs-required message choice out into its own single-ternary helper.
  // The required message spells out the full field name (e.g. "Company Name is required"),
  // matching the wording `RequiredLabeledItem`/`BillToReview` already show on-page --
  // `.voucher-review-group .content` applies `text-transform: capitalize`, so this renders
  // as "Company Name Is Required" the same way the on-page messages do. The warning icon
  // itself renders next to the field's LABEL (see the `.label` block below), matching the
  // reference design -- not next to this message text. The range-warning message also adds
  // the same static outlier note the read-only Lease Overview/Comp Data sections show below
  // their own "Typical range: ..." text (see `RequiredLabeledItem.js`), per product's
  // confirmation that it belongs on both panels.
  const renderFieldMessage = (entry) => {
    // A resolved (`complete`) field shows nothing here -- just the green checkmark next to
    // its label (see the row layout below).
    if (entry.complete) return null;
    if (entry.isRangeWarning) {
      return (
        <span className="warning-message">
          {entry.message || 'Outside typical range.'}
          <br />
          These values are considered outliers. Please validate.
        </span>
      );
    }
    // `requiredMessage` lets a read-only "list is empty" entry (e.g. `useRegisterRequiredField`
    // registrations for Consideration/Commission/Installment below) spell out the SAME
    // "You must add at least one X..." wording the live Financials-tab-mirroring Review
    // section already shows inline, instead of the generic "<label> is required" fallback.
    return <span className="error-message">{entry.requiredMessage || `${entry.label} is required`}</span>;
  };

  // Renders a single `groupSectionEntries(...)` group -- factored out so it can be called
  // either directly (a standalone full-width row) or from inside a `.required-field-pairs`
  // two-column grid wrapper (see `batchIntoRenderUnits` above), without duplicating this
  // logic in both places.
  const renderGroup = (group) => {
    // A `wrapperClass` group is a contiguous run of `consideration`/
    // `additionalCommission` rows -- rendered together inside the SAME
    // `.table.<wrapperClass>` ancestor the live tab itself uses, with a single
    // shared header row above them (see `renderCustomRowHeader`/`groupSectionEntries`).
    if (group.wrapperClass) {
      return (
        <div
          className={`required-field-row required-field-row--custom table ${group.wrapperClass}`}
          key={`${group.rowType}-${group.entries[0].fieldId}`}
        >
          {renderCustomRowHeader(group.rowType, group.entries[0].rowProps)}
          {group.entries.map(entry => (
            <React.Fragment key={entry.fieldId}>{renderCustomRow(entry)}</React.Fragment>
          ))}
        </div>
      );
    }
    const [entry] = group.entries;
    // Custom rows (Commission/Installment/Allocation/Bill To rows -- see
    // `useRegisterRequiredRows`) render the ACTUAL Financials tab row component
    // instead of the generic label/control/message layout below, since that row
    // already shows its own field-level labels and "Required" messaging exactly as
    // it does on the live tab.
    if (entry.customRowType) {
      // Standalone custom rows (Commission/Installment/Allocation/Bill To -- as
      // opposed to the grouped Consideration/AdditionalCommission `table` wrapper
      // handled above, whose individual entries can each be at a different
      // complete/incomplete state) get a green checkmark to their left once
      // resolved, using an inner flex wrapper so the OUTER `--custom` class (shared
      // with that grouped-table wrapper) keeps its existing layout untouched.
      return (
        <div className="required-field-row required-field-row--custom" key={entry.fieldId}>
          <div className="required-field-row__custom-row-inner">
            {entry.complete && <span className="required-field-row__check" aria-hidden="true">✓</span>}
            <div className="required-field-row__custom-row-content">
              {renderCustomRow(entry)}
              {!entry.complete && entry.requiredMessage && (
                <span className="error-message">{entry.requiredMessage}</span>
              )}
            </div>
          </div>
        </div>
      );
    }
    let rowStatus = 'error';
    if (entry.complete) rowStatus = 'complete';
    else if (entry.isRangeWarning) rowStatus = 'warning';
    const rowClassName = `required-field-row required-field-row--${rowStatus}`;
    return (
      <div className={rowClassName} key={entry.fieldId}>
        <p className="label">
          {entry.complete && <span className="required-field-row__check" aria-hidden="true">✓</span>}
          {!entry.complete && !entry.isRangeWarning && <span className="required-field-row__asterisk">* </span>}
          {!entry.complete && entry.isRangeWarning && (
            <Icon iconName="warning" variant="warning" size="medium" className="required-field-row__warning-icon" />
          )}
          {entry.label}
        </p>
        {entry.editTarget ? (
          <div className="required-field-row__control">
            {renderControl(entry)}
            {savingFieldIds[entry.fieldId] ? (
              <span className="required-field-row__saving-message">Saving...</span>
            ) : !['clientCode', 'naicsSearch', 'zip', 'leaseEscalationSchedule'].includes(entry.controlType) && renderFieldMessage(entry)}
          </div>
        ) : (
          <span className="required-field-row__readonly">
            {renderFieldMessage(entry)}
            {entry.linkTo && <Link to={entry.linkTo}>View</Link>}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="voucher-review-group required-fields-summary">
      <div className="required-fields-summary__header">
        <div className="required-fields-summary__title-row">
          {/* Mirrors `footerStatus` instead of just `allRequiredComplete`, so this icon
              reflects the SAME overall state the footer banner does: red while required
              fields are still missing, the shield (matching the footer's "N values left
              outside typical ranges" icon) while required fields are all filled but range
              warnings remain, green only once nothing is left to resolve at all. */}
          <StatusIcon status={footerStatus} />
          <h2>Before you submit</h2>
        </div>
        <p className="required-fields-summary__legend">
          Resolve the items below. Fields marked with an asterisk (
          <span className="required-fields-summary__legend-asterisk">*</span>
          ) are required and must be completed. Fields flagged with a
          {' '}
          <Icon iconName="warning" variant="warning" size="small" />
          {' '}
          icon fall outside typical ranges. Correct them here, or leave them and verify with the broker in the next step.
        </p>
      </div>
      <div className="content">
        {orderedSectionNames.map(sectionName => (
          <div className="required-field-section" key={sectionName}>
            <h3>{sectionName.toUpperCase()}</h3>
            {splitEntriesBySubSection(orderSectionEntries(sectionName, groups[sectionName])).map((subSection) => {
              const subSectionKey = `${sectionName}-${subSection.title || 'default'}`;
              return (
                <div className="required-field-subsection" key={subSectionKey}>
                  {subSection.title && <h4 className="required-field-subsection__title">{subSection.title}</h4>}
                  {batchIntoRenderUnits(groupSectionEntries(subSection.entries)).map((unit) => {
                    if (unit.type === 'pairs') {
                      return (
                        <div className="required-field-pairs" key={`pairs-${unit.groups[0].entries[0].fieldId}`}>
                          {unit.groups.map(group => renderGroup(group))}
                        </div>
                      );
                    }
                    return renderGroup(unit.group);
                  })}
                </div>
              );
            })}

          </div>
        ))}
      </div>
      <div className={`required-fields-summary__footer required-fields-summary__footer--${footerStatus}`}>
        <div className="required-fields-summary__footer-text">
          {footerCopy.messages.map(message => (
            <div className="required-fields-summary__footer-message" key={message.status}>
              <StatusIcon status={message.status} />
              <div className="required-fields-summary__footer-message-text">
                <p className={`required-fields-summary__footer-title required-fields-summary__footer-title--${message.status}`}>
                  {message.title}
                </p>
                <p className="required-fields-summary__footer-subtitle">
                  {message.subtitle}
                </p>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="required-fields-summary__continue-btn"
          disabled={missingCount > 0 || verificationLoading}
          onClick={onContinue}
        >
          {footerCopy.buttonLabel}
        </button>
      </div>
    </div>
  );
};

RequiredFieldsSummary.propTypes = {
  onContinue: PropTypes.func,
  onEditField: PropTypes.func,
  // Shared decision from `useVerificationRequired`; leave undefined to derive it from range warnings.
  verificationRequired: PropTypes.bool,
  verificationLoading: PropTypes.bool,
};

RequiredFieldsSummary.defaultProps = {
  onContinue: () => {},
  onEditField: () => {},
  verificationRequired: undefined,
  verificationLoading: false,
};

export default RequiredFieldsSummary;
